import type { CSSProperties } from "react";
import type {
  BackgroundStyle,
  SurfaceStyle,
  TypographyAppearance,
} from "@/cms/appearance";

function surfaceShadow(
  size: NonNullable<SurfaceStyle["shadow"]>,
  opacity?: number,
) {
  const alpha = Math.max(
    0,
    Math.min(
      1,
      opacity ?? (size === "2xl" ? 0.25 : size === "sm" ? 0.05 : 0.1),
    ),
  );
  const color = `rgb(0 0 0 / ${alpha})`;
  return {
    none: "none",
    sm: `0 1px 2px 0 ${color}`,
    md: `0 4px 6px -1px ${color}, 0 2px 4px -2px ${color}`,
    lg: `0 10px 15px -3px ${color}, 0 4px 6px -4px ${color}`,
    xl: `0 20px 25px -5px ${color}, 0 8px 10px -6px ${color}`,
    "2xl": `0 25px 50px -12px ${color}`,
  }[size];
}

export function responsiveValue(
  value: string | undefined,
  desktop = false,
  fallback = "",
) {
  const parts = value?.split("|").map((part) => part.trim());
  return (desktop ? (parts?.[1] ?? parts?.[0]) : parts?.[0]) || fallback;
}

export function responsiveVariables(
  name: string,
  value?: string,
  fallback?: string,
): CSSProperties {
  if (value === undefined && fallback === undefined) return {};
  return {
    [`--re-${name}`]: responsiveValue(value, false, fallback),
    [`--re-${name}-desktop`]: responsiveValue(value, true, fallback),
  } as CSSProperties;
}

export function surfaceStyle(
  surface?: SurfaceStyle,
  background?: string,
): CSSProperties | undefined {
  if (!surface) return undefined;
  const color = surface.backgroundColor ?? background ?? "var(--card-bg-color)";
  const opacity = Math.max(0, Math.min(1, surface.backgroundOpacity ?? 1));
  const blur = Math.max(0, Math.min(64, surface.backdropBlur ?? 0));
  return {
    "--re-surface-color": color,
    "--re-surface-opacity": `${opacity * 100}%`,
    backdropFilter: blur ? `blur(${blur}px)` : undefined,
    WebkitBackdropFilter: blur ? `blur(${blur}px)` : undefined,
    "--re-surface-border-base": surface.borderColor,
    "--re-surface-border-opacity": `${Math.max(0, Math.min(1, surface.borderOpacity ?? 1)) * 100}%`,
    borderColor:
      surface.borderOpacity === undefined
        ? surface.borderColor
        : "var(--re-surface-border)",
    borderWidth:
      surface.borderWidth === undefined
        ? undefined
        : `${Math.max(0, surface.borderWidth)}px`,
    borderStyle: surface.borderWidth ? "solid" : undefined,
    boxShadow: surface.shadow
      ? surfaceShadow(surface.shadow, surface.shadowOpacity)
      : undefined,
    borderRadius: surface.radius
      ? `var(--re-shape-radius, ${surface.radius})`
      : undefined,
  } as CSSProperties;
}

export function typographyStyle(
  appearance?: TypographyAppearance,
): CSSProperties | undefined {
  if (!appearance) return undefined;
  return {
    ...responsiveVariables("font-size", appearance.fontSize),
    ...responsiveVariables("text-columns", appearance.columnCount),
    ...responsiveVariables("text-column-gap", appearance.columnGap),
    ...responsiveVariables("paragraph-gap", appearance.paragraphSpacing),
    ...responsiveVariables("paragraph-top", appearance.paragraphMarginTop),
    ...responsiveVariables("paragraph-width", appearance.paragraphMaxWidth),
    fontWeight: appearance.fontWeight,
    lineHeight: appearance.lineHeight,
    letterSpacing: appearance.letterSpacing,
    fontFamily: appearance.fontRole
      ? `var(--${appearance.fontRole}-font-family)`
      : undefined,
    textShadow: appearance.textShadow,
    width: appearance.fitContent ? "fit-content" : undefined,
    maxWidth: appearance.maxWidth ?? "100%",
  };
}

export function backgroundGradient(gradient?: BackgroundStyle["gradient"]) {
  if (!gradient?.steps?.length) return undefined;
  const steps = gradient.steps
    .map(
      (step) =>
        `${step.color}${step.offset === undefined ? "" : ` ${step.offset}%`}`,
    )
    .join(", ");
  const at = gradient.position ?? "center";
  if (gradient.type === "radial")
    return `radial-gradient(circle at ${at}, ${steps})`;
  if (gradient.type === "conic")
    return `conic-gradient(from ${gradient.angle ?? 180}deg at ${at}, ${steps})`;
  return `linear-gradient(${gradient.angle ?? 180}deg, ${steps})`;
}

/** `direction` is a CSS gradient direction such as `to top left`; the colour is strongest at that side or corner. */
export function fadeGradient(
  fade?: BackgroundStyle["fade"],
  direction = "to bottom",
) {
  if (!fade) return undefined;
  const start = Math.max(0, Math.min(100, fade.start ?? 30));
  const end = Math.max(start, Math.min(100, fade.end ?? 100));
  const color = fade.color || "var(--page-bg-color)";
  return `linear-gradient(${direction}, color-mix(in srgb, ${color} 0%, transparent) ${start}%, ${color} ${end}%)`;
}

/** `layout.header.maxWidth` of Standard and Gennemsigtig: the row of logo, menu and icons stays centred within it. */
export function headerContentWidth(
  maxWidth: unknown,
): CSSProperties | undefined {
  return typeof maxWidth === "string" && maxWidth.trim()
    ? { maxWidth: maxWidth.trim(), marginInline: "auto" }
    : undefined;
}
